import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Campaign as AnnouncementIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { fetchAnnouncementsRequest } from "../../redux/Announcements/announcementActions";
import { CREATE_ANNOUNCEMENT } from "../../component/common/Routes/AppRoutes";
import { paperStyles } from "./announcementStyle";

const AnnouncementList = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { announcements, totalAnnouncements, loading } = useSelector(
    (state) => state.announcements
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingAnnouncement, setViewingAnnouncement] = useState(null);

  useEffect(() => {
    dispatch(
      fetchAnnouncementsRequest({
        pageNo: paginationModel.page + 1,
        pagesize: paginationModel.pageSize,
      })
    );
  }, [dispatch, paginationModel]);

  const handleViewAnnouncement = (announcement) => {
    setViewingAnnouncement(announcement);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingAnnouncement(null);
  };

  const handleAddAnnouncement = () => {
    navigate(CREATE_ANNOUNCEMENT);
  };



  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row?.name || params.row?.subject || "-"}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Content",
      flex: 2,
      minWidth: 300,
      renderCell: (params) => {
        const value = params.row?.description || params.row?.content || "";
        const plainText = value.replace(/<[^>]*>/g, "") || "";
        return (
          <Tooltip title={plainText} arrow>
            <Box
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {plainText}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 150,
      renderCell: (params) => {
        const dateValue = params.row?.created_at || params.row?.createdAt;
        return dateValue ? new Date(dateValue).toLocaleDateString() : "-";
      },
    },
    {
      field: "image",
      headerName: "Image",
      width: 120,
      renderCell: (params) =>
        params.value ? (
          <Box
            component="img"
            src={params.value}
            alt="announcement"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              objectFit: "cover",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(params.value, "_blank");
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => handleViewAnnouncement(params.row)}
          size="small"
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 3,
          background: "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          border: "1px solid #eee"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AnnouncementIcon sx={{ color: "#ff9800", fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Announcements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and broadcast updates to your customers
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddAnnouncement}
          sx={{ 
            textTransform: "none", 
            bgcolor: "#ff9800", 
            "&:hover": { bgcolor: "#f57c00" },
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600
          }}
        >
          Add Announcement
        </Button>
      </Paper>

      <Paper sx={{ height: 600, width: "100%", ...paperStyles }}>
        <DataGrid
          rows={announcements}
          columns={columns}
          loading={loading}
          rowCount={totalAnnouncements}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #3F51B5 0%, #1A237E 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Announcement Details</Typography>
          <IconButton onClick={handleCloseViewDialog} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
            <Typography variant="h6">{viewingAnnouncement?.name || viewingAnnouncement?.subject}</Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">Content</Typography>
            <Box
              dangerouslySetInnerHTML={{ __html: viewingAnnouncement?.description || viewingAnnouncement?.content }}
              sx={{ mt: 1, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}
            />
          </Box>
          {viewingAnnouncement?.image && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Image</Typography>
              <Box
                component="img"
                src={viewingAnnouncement.image}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  borderRadius: 2,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnnouncementList;
